import { action, computed, observable } from 'mobx';
import Alert from 'react-s-alert';

import { analytics } from 'core/analytics';
import { AppManager } from 'core/appManager';

import { debounce } from 'throttle-debounce';

import {
  NotificationsQuery,
  NotificationsPendingCountQuery,
  MarkAllAsReadMutation,
  NotificationSubscription,
} from 'apps/notificationCenter/gql';
import { NotificationConnManager } from 'apps/notificationCenter/connManager';
import { createNotification } from 'apps/utils';
import {
  isInDesktopMode,
  setNotificationCount,
} from 'libs/gutClient/electronBridge';
import { clientStore } from 'apps/client/app';

export class NotificationCenterAppManager extends AppManager {
  static registryName = 'NotificationCenterAppManager';
  @observable hasError = false;
  @observable pendingCount = 0;

  pendingCountInitialQuery = true;

  constructor(parent, init = {}, options) {
    super(parent, init, options);

    this.notificationsConn = new NotificationConnManager(this, {
      initialBatchSize: 10,
      batchSize: 30,
      sortBy: 'recent',
      sortOptions: [],
      loadMoreQuery: NotificationsQuery,
    });
  }

  formatMessage = count => {
    if (count > 1) {
      return `You have ${count} notifications`;
    }

    return 'You have a new notification';
  };

  createNotificationDebounced = () =>
    debounce(1200, async (count, id) => {
      await createNotification({
        title: this.formatMessage(count),
        notificationId: id,
        options: {
          tag: id,
        },
        tabId: clientStore.currentTabId,
      });
    });

  @computed get notifications() {
    return this.notificationsConn ? this.notificationsConn.arrayApp || [] : [];
  }

  @computed get isSubscribed() {
    return !!this.notificationSubscription;
  }

  @action CatchError = error => {
    this.hasError = true;
    analytics.logError(error);
  };

  @action SubscribeNotification = () => {
    this.notificationsPendingCountQuery = new NotificationsPendingCountQuery(
      this,
      {
        fetchPolicy: 'network-only',
      },
    );

    this.notificationSubscription = new NotificationSubscription(this, {
      variables: {},
    });

    this.notificationSubscription.StartSubscription();

    const debouncedNotification = this.createNotificationDebounced();

    this.notificationSubscription.process(result => {
      if (result) {
        this.notificationsPendingCountQuery.refetchQuery();
      }
    });

    this.notificationsPendingCountQuery.query();

    this.notificationsPendingCountQuery.process(result => {
      if (result?.pendingCount) {
        if (!this.pendingCountInitialQuery) {
          debouncedNotification(result.pendingCount, result.id);
        }

        if (this.pendingCountInitialQuery) {
          this.pendingCountInitialQuery = false;
        }

        if (isInDesktopMode()) {
          setNotificationCount(result.pendingCount, clientStore.currentTabId);
        }

        return result.pendingCount;
      }

      if (isInDesktopMode()) {
        setNotificationCount(0, clientStore.currentTabId);
      }

      return 0;
    }, this.SetPendingCount);
  };

  @action StopSubscribeNotification = () => {
    if (this.notificationSubscription) {
      this.notificationSubscription.CompleteSubscription();
      this.notificationSubscription = null;
    }
  };

  @action SetPendingCount = pendingCount => {
    const count = parseInt(pendingCount, 10);
    if (typeof count === 'number' && count >= 0) {
      this.pendingCount = count;
    }
  };

  @action LoadMore = () => {
    this.notificationsConn.loadMore({ subPath: 'notifications' });
  };

  @action NewNotificationsQuery = () => {
    this.notificationsConn.Reset();
    this.notificationsConn.loadingMore = true;
    this.notificationsQuery = new NotificationsQuery(this.notificationsConn, {
      fetchPolicy: 'network-only',
    });
    this.notificationsQuery.process(
      this.notificationsConn.process,
      this.notificationsConn.moreData,
      { subPath: 'notifications' },
    );
    this.notificationsQuery.process(result => result, this.SetPendingCount, {
      subPath: 'pendingCount',
    });
    this.notificationsQuery.query();
  };

  @computed get loading() {
    return !this.notificationsConn || this.notificationsConn.loadingMore;
  }

  @action MarkAllNotificationsAsRead = () => {
    const options = {
      onSysErr: () =>
        Alert.error(
          'Oops, there was an error marking your notifications as read.',
        ),
      onUserErr: () =>
        Alert.error(
          'Oops, there was an error marking your notifications as read.',
        ),
      onSuccess: pendingCount => {
        this.pendingCount = pendingCount;
        this.notificationsConn.arrayApp.apps.forEach(notification => {
          notification.SetRead(true);
        });
        Alert.success('All Notifications marked as read.');
      },
    };
    const mutation = new MarkAllAsReadMutation();
    mutation.setCallbacks(options);
    mutation.mutate();
    return mutation;
  };
}

export const notificationCenter = new NotificationCenterAppManager(
  null,
  {},
  { autoDispose: false },
);
